import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SystemDialogProvider } from './context/SystemDialogContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { LocationProvider } from './context/LocationContext';
import { MessageUnreadProvider } from './context/MessageUnreadContext';
import { usePermissions } from './hooks/usePermissions';
import Layout from './components/Layout';
import { RequirePermission } from './components/RequirePermission';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EmployeeList from './pages/EmployeeList';
import EmployeeProfile from './pages/EmployeeProfile';
import OrganizationChart from './pages/OrganizationChart';
import Settings from './pages/Settings';
import LeavePolicies from './pages/LeavePolicies';
import HolidayAdmin from './pages/HolidayAdmin';
import SickLeaveAdmin from './pages/SickLeaveAdmin';
import IncidentAdmin from './pages/IncidentAdmin';
import SecurityCommandControlLog from './pages/SecurityCommandControlLog';
import MyIncidents from './pages/MyIncidents';
import MyAttendances from './pages/MyAttendances';
import MySchedules from './pages/MySchedules';
import ShiftMarketplace from './pages/ShiftMarketplace';
import MyTasks from './pages/MyTasks';
import MyTrainings from './pages/MyTrainings';
import MyLicenses from './pages/MyLicenses';
import MyDocuments from './pages/MyDocuments';
import MyDocumentView from './pages/MyDocumentView';
import DocumentsPoliciesPage from './pages/DocumentsPoliciesPage';
import DocumentPolicyEditorPage from './pages/DocumentPolicyEditorPage';
import DocumentPolicyDetailPage from './pages/DocumentPolicyDetailPage';
import MyHolidays from './pages/MyHolidays';
import MyUnavailability from './pages/MyUnavailability';
import UnavailabilityAdmin from './pages/UnavailabilityAdmin';
import MyTeam from './pages/MyTeam';
import ManagerInbox from './pages/ManagerInbox';
import MySickLeave from './pages/MySickLeave';
import Scheduling from './pages/Scheduling';
import ScheduleImportHistory from './pages/ScheduleImportHistory';
import Attendance from './pages/Attendance';
import AttendanceImportHistory from './pages/AttendanceImportHistory';
import FaceSyncStatus from './pages/FaceSyncStatus';
import TaskAdmin from './pages/TaskAdmin';
import MessageAdmin from './pages/MessageAdmin';
import Messages from './pages/Messages';
import MyAccount from './pages/MyAccount';
import JobMonitor from './pages/JobMonitor';
import JobApplicantTracking from './pages/JobApplicantTracking';
import Timecards from './pages/Timecards';
import PlanVsActual from './pages/PlanVsActual';
import AuditLogsReport from './pages/AuditLogsReport';
import Reports from './pages/Reports';
import PayrollRunAuditPage from './pages/PayrollRunAuditPage';
import OverviewDashboard from './pages/OverviewDashboard';
import AiAssistant from './pages/AiAssistant';
import QaChecks from './pages/QaChecks';
import { canShowQaChecksNav } from './qa/tenantAccess';
import type { PermissionsContextValue } from './context/PermissionsContext';
import './App.css';

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

/** Authenticated + permission-gated page inside the app shell. */
const guarded = (
  page: React.ReactElement,
  when: (p: PermissionsContextValue) => boolean,
) => (
  <ProtectedRoute>
    <RequirePermission when={when}>
      <Layout>{page}</Layout>
    </RequirePermission>
  </ProtectedRoute>
);

const authed = (page: React.ReactElement) => (
  <ProtectedRoute>
    <Layout>{page}</Layout>
  </ProtectedRoute>
);

/** Empty home when the user has no dashboard and no other resolvable home permission. */
const BlankHome: React.FC = () => <div className="flex-1 min-h-0 bg-neutral-50" />;

const LoginRedirect: React.FC = () => {
  const location = useLocation();
  const { loading, hasDashboard, defaultHomePath } = usePermissions();
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
  const redirectPath = typeof window !== 'undefined' ? sessionStorage.getItem('loginRedirect') : null;
  useEffect(() => {
    if (redirectPath && typeof window !== 'undefined') {
      sessionStorage.removeItem('loginRedirect');
    }
  }, [redirectPath]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  let to = redirectPath || from;
  if (!to) {
    if (hasDashboard) {
      to = '/';
    } else if (defaultHomePath) {
      to = defaultHomePath;
    } else {
      to = '/';
    }
  } else if (to === '/' && !hasDashboard) {
    to = defaultHomePath ?? '/';
  }
  return <Navigate to={to} replace />;
};

/** Placeholder while permissions resolve — does not mount dashboard or fetch dashboard data. */
const DashboardHomeSkeleton: React.FC = () => (
  <div className="flex-1 overflow-y-auto bg-[#F4F7F9] min-h-0 animate-pulse">
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-6">
      <div className="space-y-2">
        <div className="h-7 bg-gray-200 rounded w-48" />
        <div className="h-4 bg-gray-100 rounded w-56" />
      </div>
      <div className="grid grid-cols-2 xl:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-28 bg-white rounded-lg border border-gray-200" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-6 h-52 bg-white rounded-lg border border-gray-200" />
        <div className="lg:col-span-3 h-52 bg-white rounded-lg border border-gray-200" />
        <div className="lg:col-span-3 h-52 bg-white rounded-lg border border-gray-200" />
      </div>
    </div>
  </div>
);

/** Renders Dashboard when permitted; otherwise redirects to a permission-based home or shows a blank page. */
const HomeRoute: React.FC = () => {
  const { loading: authLoading } = useAuth();
  const { loading: permissionsLoading, hasDashboard, defaultHomePath } = usePermissions();

  if (authLoading || permissionsLoading) {
    return <DashboardHomeSkeleton />;
  }

  if (hasDashboard) {
    return <OverviewDashboard />;
  }

  if (defaultHomePath && defaultHomePath !== '/') {
    return <Navigate to={defaultHomePath} replace />;
  }

  return <BlankHome />;
};

const AppRoutes: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <LoginRedirect /> : <Login />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <HomeRoute />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/overview-dashboard"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/dashboard/legacy"
        element={guarded(<Dashboard />, (p) => p.hasDashboard)}
      />
      <Route
        path="/employees"
        element={guarded(<EmployeeList />, (p) => p.hasEmployeeManagement)}
      />
      <Route
        path="/employees/organization-chart"
        element={guarded(<OrganizationChart />, (p) => p.hasEmployeeManagement)}
      />
      <Route path="/organization" element={<Navigate to="/employees" replace />} />
      <Route
        path="/employees/:id"
        element={guarded(<EmployeeProfile />, (p) => p.hasEmployeeManagement)}
      />
      <Route
        path="/settings"
        element={guarded(<Settings />, (p) => p.hasSettings || p.hasTimeStationClockInOut)}
      />
      <Route
        path="/leave-policies"
        element={guarded(<LeavePolicies />, (p) => p.hasLeavePolicies)}
      />
      <Route
        path="/holiday-admin"
        element={guarded(<HolidayAdmin />, (p) => p.hasHolidayManagement)}
      />
      <Route
        path="/unavailability-admin"
        element={guarded(<UnavailabilityAdmin />, (p) => p.hasHolidayManagement || p.hasScheduling)}
      />
      <Route
        path="/sick-leave-admin"
        element={guarded(<SickLeaveAdmin />, (p) => p.hasHolidayManagement)}
      />
      <Route
        path="/job-applicant-tracking"
        element={guarded(<JobApplicantTracking />, (p) => p.hasJobApplicantTracking)}
      />
      <Route
        path="/incident-admin"
        element={guarded(<IncidentAdmin />, (p) => p.hasIncidentManagement)}
      />
      <Route
        path="/security-command-control-log"
        element={guarded(
          <SecurityCommandControlLog />,
          (p) => p.securityCommandControlLogEnabled && p.hasPermission('security_command_control_log'),
        )}
      />
      <Route path="/my-incidents" element={authed(<MyIncidents />)} />
      <Route path="/my-attendances" element={authed(<MyAttendances />)} />
      <Route path="/my-schedules" element={authed(<MySchedules />)} />
      <Route
        path="/shift-marketplace"
        element={guarded(
          <ShiftMarketplace />,
          (p) => p.hasPermission('marketplace_proposals') || p.hasScheduling,
        )}
      />
      <Route
        path="/shift-marketplace/approvals"
        element={<Navigate to="/shift-marketplace?tab=approvals" replace />}
      />
      <Route path="/my-tasks" element={authed(<MyTasks />)} />
      <Route
        path="/messages"
        element={guarded(
          <Messages />,
          (p) => p.hasPermission('my_messages') || p.hasMessageManagement,
        )}
      />
      <Route
        path="/my-messages"
        element={guarded(
          <Messages />,
          (p) => p.hasPermission('my_messages') || p.hasMessageManagement,
        )}
      />
      <Route path="/my-trainings" element={authed(<MyTrainings />)} />
      <Route
        path="/my-licenses"
        element={guarded(<MyLicenses />, (p) => p.hasPermission('my_licenses'))}
      />
      <Route path="/my-documents" element={authed(<MyDocuments />)} />
      <Route path="/my-documents/:id" element={authed(<MyDocumentView />)} />
      <Route
        path="/documents-policies"
        element={guarded(<DocumentsPoliciesPage />, (p) => p.hasDocumentsPolicies)}
      />
      <Route
        path="/documents-policies/new"
        element={guarded(<DocumentPolicyEditorPage />, (p) => p.hasDocumentsPolicies)}
      />
      <Route
        path="/documents-policies/:id/edit"
        element={guarded(<DocumentPolicyEditorPage />, (p) => p.hasDocumentsPolicies)}
      />
      <Route
        path="/documents-policies/:id"
        element={guarded(<DocumentPolicyDetailPage />, (p) => p.hasDocumentsPolicies)}
      />
      <Route path="/inbox" element={authed(<ManagerInbox />)} />
      <Route path="/my-holidays" element={authed(<MyHolidays />)} />
      <Route path="/my-unavailability" element={authed(<MyUnavailability />)} />
      <Route path="/my-team" element={authed(<MyTeam />)} />
      <Route path="/my-sick-leave" element={authed(<MySickLeave />)} />
      <Route path="/ai-assistant" element={authed(<AiAssistant />)} />
      <Route
        path="/scheduling"
        element={guarded(<Scheduling />, (p) => p.hasScheduling)}
      />
      <Route
        path="/scheduling/import-history"
        element={guarded(<ScheduleImportHistory />, (p) => p.hasScheduling)}
      />
      <Route
        path="/attendance"
        element={guarded(<Attendance />, (p) => p.hasAttendanceAdmin)}
      />
      <Route
        path="/attendance/import-history"
        element={guarded(<AttendanceImportHistory />, (p) => p.hasAttendanceAdmin)}
      />
      <Route
        path="/face-sync-status"
        element={guarded(<FaceSyncStatus />, (p) => p.hasAttendanceAdmin || p.hasSettings)}
      />
      <Route
        path="/task-admin"
        element={guarded(<TaskAdmin />, (p) => p.hasTaskManagement)}
      />
      <Route
        path="/message-admin"
        element={guarded(<MessageAdmin />, (p) => p.hasMessageManagement)}
      />
      <Route path="/my-account" element={authed(<MyAccount />)} />
      <Route
        path="/job-monitor"
        element={guarded(<JobMonitor />, (p) => p.hasJobMonitor)}
      />
      <Route
        path="/timecards"
        element={guarded(<Timecards />, (p) => p.hasTimecards)}
      />
      <Route
        path="/qa-checks"
        element={guarded(<QaChecks />, (p) => canShowQaChecksNav(p.hasPermission('qa_checks')))}
      />
      <Route
        path="/reports"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/plan-vs-actual"
        element={guarded(<PlanVsActual />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/audit-logs"
        element={guarded(<AuditLogsReport />, (p) => p.hasReports || p.hasSettings)}
      />
      <Route
        path="/reports/attendance-verifier"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/leave-ledger"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route path="/reports/payroll" element={<Navigate to="/reports/payroll-builder" replace />} />
      <Route
        path="/reports/payroll-details"
        element={<Navigate to="/reports/payroll-builder" replace />}
      />
      <Route
        path="/reports/payroll-runs/:runId/audit"
        element={guarded(<PayrollRunAuditPage />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/payroll-runs"
        element={<Navigate to="/reports/payroll-builder" replace />}
      />
      <Route
        path="/reports/payroll-builder"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/my-reports"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/my-reports/:templateId"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/timecards-analysis"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/timecards-weekly-totals"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/timecards-early-late"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/timecards-break"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/timecards-staff-salaried"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/employee-details"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route
        path="/reports/schedules/invoice"
        element={guarded(<Reports />, (p) => p.hasReports)}
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <Router>
      <SystemDialogProvider>
        <AuthProvider>
          <PermissionsProvider>
            <LocationProvider>
              <MessageUnreadProvider>
                <div className="App">
                  <AppRoutes />
                </div>
              </MessageUnreadProvider>
            </LocationProvider>
          </PermissionsProvider>
        </AuthProvider>
      </SystemDialogProvider>
    </Router>
  );
}

export default App;
